import type { AssetDetail } from "@/entities/asset";
import type {
  AssetFormValues,
  UseAssetFormServiceDateResult,
} from "@/modules/asset/asset-form/asset-form.type";
import type { ValidatorRule } from "@rc-component/form/lib/interface";
import type { FormInstance } from "antd";

import { Form } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { assetHasTechnicalData, useAssetEditAssetType } from "@/entities/asset";

const { useWatch } = Form;

export const useAssetFormServiceDate = (
  form: FormInstance<AssetFormValues>
): UseAssetFormServiceDateResult => {
  const { t } = useTranslation();
  const errorMsg = t("asset:form.validation.serviceDateBeforeManufactureDate");

  const manufactureDate = useWatch("manufactureDate", form);
  const serviceDate = useWatch("serviceDate", form);

  const minDate = manufactureDate ? dayjs(manufactureDate) : undefined;

  const validator: ValidatorRule["validator"] = () => {
    if (!serviceDate || !manufactureDate) return Promise.resolve();

    const isAfter = manufactureDate.isAfter(serviceDate);

    if (isAfter) return Promise.reject(new Error(errorMsg));

    return Promise.resolve();
  };

  return {
    minDate,
    rules: [{ validator }],
    dependencies: ["manufactureDate"],
  };
};

export const useAssetFormAssetType = (asset?: AssetDetail) => {
  const {
    assetTypeId,
    setAssetTypeId,
    isAssetTypeLoading,
    showAssetTypeAlert,
  } = useAssetEditAssetType(asset?.asset_type);

  const hasTechnicalData = assetHasTechnicalData(asset);

  return {
    assetTypeId,
    setAssetTypeId,
    isAssetTypeLoading,
    showAssetTypeAlert: hasTechnicalData ? showAssetTypeAlert : false,
  };
};
